import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ExpandLessOutlined,
  ExpandMoreOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changeLegend, markLegendSaved } from "../features/cidSlice";
import { updateCID } from "../services/cidService";
import { canWriteCid } from "../utils/permissions";

import { MARKER_OPTIONS } from "../markerColors";

const LEGEND_FIELDS = MARKER_OPTIONS.map((option) => ({
  index: option.legendIndex,
  color: option.color,
  key: option.value,
  label: option.fallback,
}));

const Legend = ({ compact = false }) => {
  const legend = useSelector((state) => state.cid.value.legend);
  const touched = useSelector((state) => state.cid.value.touched);
  const data = useSelector((state) => state.cid.value);
  const id = useSelector((state) => state.assessor.value.form.cid);
  const auth = useSelector((state) => state.auth.value);
  const cidWritable = canWriteCid(auth, id);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(!compact);

  const onSave = async () => {
    if (!id) {
      return;
    }

    await updateCID({ id, ...data });
    dispatch(markLegendSaved());
    if (compact) {
      setExpanded(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ backgroundColor: "white" }}>
      <CardContent sx={{ p: compact ? 1.5 : 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant={compact ? "subtitle1" : "h6"}>
                CID Marker Legend
              </Typography>
              {!compact ? (
                <Typography variant="body2" color="text.secondary">
                  Define what each marker color means so everyone tags properties
                  consistently.
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {touched ? <Chip size="small" color="warning" label="Unsaved" /> : null}
              <IconButton
                size="small"
                color="primary"
                onClick={() => setExpanded((prev) => !prev)}
                aria-label={expanded ? "Hide legend inputs" : "Show legend inputs"}
              >
                {expanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
              </IconButton>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {LEGEND_FIELDS.map((field) => (
              <Chip
                key={field.key}
                size="small"
                label={`${field.label.split(" ")[0]}: ${legend[field.index] || "Not set"}`}
                sx={{
                  backgroundColor: field.color,
                  color: "white",
                  fontWeight: 600,
                  mb: 1,
                }}
              />
            ))}
          </Stack>

          <Collapse in={expanded}>
            <Grid container spacing={compact ? 1 : 2} sx={{ mb: 1 }}>
              {LEGEND_FIELDS.map((field) => (
                <Grid item xs={12} md={6} key={field.key}>
                  <TextField
                    fullWidth
                    size={compact ? "small" : "medium"}
                    label={field.label}
                    value={legend[field.index] || ""}
                    onChange={(e) =>
                      dispatch(changeLegend({ index: field.index, data: e.target.value }))
                    }
                    InputProps={{
                      startAdornment: (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: field.color,
                            mr: 1.5,
                          }}
                        />
                      ),
                    }}
                    placeholder="Example: Call back this week"
                  />
                </Grid>
              ))}
            </Grid>

            <Box>
              <Button
                size={compact ? "small" : "medium"}
                variant="contained"
                startIcon={<SaveOutlined />}
                disabled={!touched || !id || !cidWritable}
                onClick={onSave}
              >
                Save Legend
              </Button>
            </Box>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default Legend;
